import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Divider,
  Input,
  Select,
  message,
  Spin,
  Empty,
} from "antd";
import { PhoneOutlined, UserOutlined, ReloadOutlined } from "@ant-design/icons";
import CallRequestNotification from "./CallRequestNotification";
import {
  updateCallRequestStatus,
  assignCallRequest,
  getCallRequestsByQuoteId,
} from "../services/callRequestApiService";
import { logDebug, logInfo, logError } from "@/shared/utils/logger.js";

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CallRequestPanel = ({ quoteId, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [callRequests, setCallRequests] = useState([]);
  const [selectedCallRequest, setSelectedCallRequest] = useState(null);
  const [notes, setNotes] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // Mock user data for now - this should come from auth context
  // Using a valid MongoDB ObjectId format for testing
  const user = { id: "507f1f77bcf86cd799439011", permissions: ["call:manage"] };

  // Load call requests when component mounts or quoteId changes
  useEffect(() => {
    if (quoteId) {
      loadCallRequests();
    }
  }, [quoteId]);

  const loadCallRequests = async () => {
    setLoading(true);
    try {
      logInfo("CallRequestPanel: loading call requests", { quoteId });
      const data = await getCallRequestsByQuoteId(quoteId);
      logDebug("CallRequestPanel: call requests received", {
        data,
        isArray: Array.isArray(data),
      });
      setCallRequests(data || []);
      // Select the first call request if available
      if (data && data.length > 0) {
        logDebug("CallRequestPanel: setting default call request", {
          callRequestId: data[0]?._id || data[0]?.id,
        });
        setSelectedCallRequest(data[0]);
      }
    } catch (error) {
      logError("CallRequestPanel: error loading call requests", error);
      message.error("Failed to load call requests");
    } finally {
      setLoading(false);
    }
  };

  const refreshCallRequests = async () => {
    setRefreshing(true);
    await loadCallRequests();
    setRefreshing(false);
  };

  const handleCallRequestSelect = (callRequest) => {
    setSelectedCallRequest(callRequest);
    setNotes("");
    setSelectedStatus("");
  };

  const isAssignedToMe = selectedCallRequest?.assignedToUserId === user?.id;
  const canManage = user?.permissions?.includes("call:manage");

  const getStatusColor = (status) => {
    const colors = {
      requested: "blue",
      assigned: "orange",
      calling: "purple",
      completed: "green",
      no_answer: "red",
      reschedule: "geekblue",
    };
    return colors[status] || "default";
  };

  const handleCallNow = async () => {
    if (!selectedCallRequest) return;

    setLoading(true);
    try {
      // Update status to calling via API
      const result = await updateCallRequestStatus(
        selectedCallRequest._id || selectedCallRequest.id,
        "calling",
        "Call initiated by agent"
      );

      if (result.success) {
        window.open(`tel:${selectedCallRequest.phone}`, "_blank");
        message.success('Call status updated to "calling"');
        await refreshCallRequests(); // Refresh to get updated data
        onUpdate?.();
      } else {
        message.error("Failed to update call status");
      }
    } catch (error) {
      message.error("Failed to update call status");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus || !selectedCallRequest) return;
    setLoading(true);
    try {
      const result = await updateCallRequestStatus(
        selectedCallRequest._id || selectedCallRequest.id,
        selectedStatus,
        notes
      );

      if (result.success) {
        message.success(`Call status updated to ${selectedStatus}`);
        setNotes("");
        setSelectedStatus("");
        await refreshCallRequests(); // Refresh to get updated data
        onUpdate?.();
      } else {
        message.error("Failed to update call status");
      }
    } catch (error) {
      message.error("Failed to update call status");
    } finally {
      setLoading(false);
    }
  };

  const handleTakeCall = async () => {
    setLoading(true);
    try {
      logInfo("CallRequestPanel: taking call", {
        selectedCallRequest,
        userId: user.id,
      });

      // Use _id if available, otherwise fall back to id
      const callRequestId = selectedCallRequest._id || selectedCallRequest.id;
      const result = await assignCallRequest(callRequestId, user.id);
      logDebug("CallRequestPanel: assignment result", result);

      if (result.success) {
        message.success("Call assigned to you");
        onUpdate?.();
      } else {
        message.error("Failed to assign call");
      }
    } catch (error) {
      logError("CallRequestPanel: assignment error", error);
      message.error("Failed to assign call");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card title="Call Request" size="small">
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "10px" }}>
            <Text>Loading call requests...</Text>
          </div>
        </div>
      </Card>
    );
  }

  if (!callRequests || callRequests.length === 0) {
    return (
      <Card title="Call Request" size="small">
        <Empty
          description="No call requests for this quote"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <PhoneOutlined />
          Call Requests ({callRequests.length})
          <Button
            icon={<ReloadOutlined />}
            size="small"
            onClick={refreshCallRequests}
            loading={refreshing}
          />
        </Space>
      }
      size="small"
    >
      {/* Call Request Selection */}
      {callRequests.length > 1 && (
        <div style={{ marginBottom: "16px" }}>
          <Text strong>Select Call Request:</Text>
          <Select
            style={{ width: "100%", marginTop: "8px" }}
            value={selectedCallRequest?._id || selectedCallRequest?.id}
            onChange={(value) => {
              const callRequest = callRequests.find(
                (cr) => (cr._id || cr.id) === value
              );
              handleCallRequestSelect(callRequest);
            }}
            placeholder="Choose a call request to manage"
          >
            {callRequests.map((cr) => (
              <Option key={cr._id || cr.id} value={cr._id || cr.id}>
                {cr.phone} - {cr.status} (
                {new Date(cr.requestedAt).toLocaleString()})
              </Option>
            ))}
          </Select>
        </div>
      )}

      {/* Selected Call Request Details */}
      {selectedCallRequest && (
        <>
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <Text strong>Phone:</Text>
              <Text style={{ marginLeft: 8 }}>{selectedCallRequest.phone}</Text>
            </div>

            <div>
              <Text strong>Status:</Text>
              <Tag color={getStatusColor(selectedCallRequest.status)}>
                {selectedCallRequest.status.toUpperCase()}
              </Tag>
            </div>

            <div>
              <Text strong>Requested:</Text>
              <Text style={{ marginLeft: 8 }}>
                {new Date(selectedCallRequest.requestedAt).toLocaleString()}
              </Text>
            </div>

            {selectedCallRequest.assignedToUserId && (
              <div>
                <Text strong>Assigned To:</Text>
                <Text style={{ marginLeft: 8 }}>
                  {selectedCallRequest.assignedToUserId?.firstName ||
                    selectedCallRequest.assignedToUserId?.name ||
                    "Agent"}
                </Text>
              </div>
            )}

            {selectedCallRequest.notes && (
              <div>
                <Text strong>Notes:</Text>
                <Text style={{ marginLeft: 8 }}>
                  {selectedCallRequest.notes}
                </Text>
              </div>
            )}
          </Space>

          <Divider style={{ margin: "12px 0" }} />

          {/* Call Request Notification */}
          <CallRequestNotification
            callRequest={selectedCallRequest}
            quoteId={quoteId}
          />

          <Space direction="vertical" style={{ width: "100%" }}>
            {isAssignedToMe && selectedCallRequest.status === "assigned" && (
              <Button
                type="primary"
                icon={<PhoneOutlined />}
                onClick={handleCallNow}
                loading={loading}
                block
              >
                Call Now
              </Button>
            )}

            {/* Take Call button temporarily disabled
            {!selectedCallRequest.assignedToUserId && canManage && (
              <Button
                type="default"
                icon={<UserOutlined />}
                onClick={handleTakeCall}
                loading={loading}
                block
              >
                Take Call
              </Button>
            )}
            */}

            {isAssignedToMe && (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Select
                  placeholder="Update status"
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  style={{ width: "100%" }}
                >
                  <Option value="completed">✅ Completed</Option>
                  <Option value="no_answer">🚫 No Answer</Option>
                  <Option value="reschedule">🔁 Reschedule</Option>
                </Select>

                {selectedStatus && (
                  <TextArea
                    placeholder="Add notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                  />
                )}

                {selectedStatus && (
                  <Button
                    type="default"
                    onClick={handleStatusUpdate}
                    loading={loading}
                    block
                  >
                    Update Status
                  </Button>
                )}
              </Space>
            )}
          </Space>
        </>
      )}
    </Card>
  );
};

export default CallRequestPanel;
